import {
  filter as _filter,
  hasIn as _hasIn,
  sortBy as _sortBy,
  startsWith as _startsWith
} from 'lodash';

export interface DrpLocatorState {
  loading: boolean;
  searchContext?: SearchContext;
  searchResults?: SearchResult[];
  searchedValue: SearchedValue;
  lastSuccessfulSearchValue?: SearchedValue; // TODO: may move somewhere else
  error?: any;
}

export interface SearchedValue {
  shopName?: string;
  line1?: string;
  city?: string;
  state?: string;
  zip?: string;
  distance?: string;
  lat?: number;
  lng?: number;
  sort?(response: any): any;
}
export interface SearchResult {
  id: string;
  name: string;
  address: ResultAddress;
  contactMethodDetail: ContactMethodDetail;
  aseCertified: string;
  hoursOfOperation: string[] | [{}];
  tier: string; // TODO: maybe create Enum of
  distanceInfo: DistanceInfo;
  coordinates: Coordinates;
}
export interface ResultAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface ContactMethodDetail {
  primaryPhoneNumber: string;
  fax: string;
  emailAddresses: string[];
  websiteAddresses: string[];
}

export interface DistanceInfo {
  distance: string;
  units: string;
}

export interface Coordinates {
  latitude: string;
  longitude: string;
}

export interface InitiateConfig {
  searchContext: SearchContext;
}

export const initialServiceLocatorState = {
  loading: false,
  searchedValue: null
};

export enum SearchContext {
  DRP_SHOP_SEARCH,
  DRP_SHOP_SELECTION,
  RENTAL_SELECTION
}

export enum SEARCH_TYPES {
  LOCATION = 'location',
  NAME = 'name',
  COORDINATES = 'coordinates',
  ERROR = 'error'
}
export const DEFAULT_LOCATION_DISTANCE = '15';
export const DEFAULT_NAME_DISTANCE = '75';

// TODO: maybe move to separate file
export class DrpSearch implements SearchedValue {
  payload: SearchedValue;
  line1: any;
  city: any;
  state: any;
  zip: any;
  shopName: any;
  distance: any;
  lat: number;
  lng: number;

  constructor(payload: any) {
    this.payload = payload;
    this.line1 = payload.line1 ? payload.line1 : null;
    this.shopName = payload.shopName ? payload.shopName : null;
    this.city = payload.city ? payload.city : null;
    this.state = payload.state ? payload.state : null;
    this.zip = payload.zip ? payload.zip : null;
    this.distance = payload.distance ? payload.distance : null;
    this.lat = payload.lat ? payload.lat : null;
    this.lng = payload.lng ? payload.lng : null;
  }

  /**
   * based on search payload type, sort the reponse properly and return it with the response
   */
  public sort(response) {
    const type = this.getSearchType();
    if (type === SEARCH_TYPES.LOCATION || type === SEARCH_TYPES.COORDINATES) {
      return this.sortLocationResponse(response);
    } else if (type === SEARCH_TYPES.NAME) {
      return this.sortByNameResponse(response);
    } else {
      return 'error';
    }
  }

  /**
   * Set the searched payload to the proper type for sorting
   */
  public getSearchType() {
    if (
      (this.line1 && this.city && this.state && this.zip && this.distance) ||
      (this.zip && this.distance)
    ) {
      return SEARCH_TYPES.LOCATION;
    } else if ((this.shopName && this.zip) || (this.shopName && this.city && this.state)) {
      return SEARCH_TYPES.NAME;
    } else if (this.lat && this.lng && this.distance) {
      return SEARCH_TYPES.COORDINATES;
    } else {
      return SEARCH_TYPES.ERROR;
    }
  }

  /**
   * Sort the HTTP response when a 'Location' or 'Coordinates' were given
   * Tier1 sorted by distance (closest to farthest) first
   * then Tier2 & Tier3 sorted by distance
   * @param response
   */
  private sortLocationResponse(response: any) {
    let tier1Dist = [];
    let tierNDist = [];
    const tier1 = _filter(response.shops, result => result.tier === 'Tier1');
    const tierN = _filter(response.shops, result => result.tier !== 'Tier1');
    tier1Dist = this.sortDistance(tier1);
    tierNDist = this.sortDistance(tierN);
    const results = tier1Dist.concat(tierNDist);
    response.shops = results;
    return response;
  }

  /**
   * Sort the HTTP response when a 'Name' was given
   * Tier1 sorted by exact match then non-exact
   * then Tier2 & Tier3 sorted by exact match then non-exact
   */
  // TODO: refactor if there is time
  private sortByNameResponse(response: any) {
    const tier1 = _filter(response.shops, result => result.tier === 'Tier1');
    const tierN = _filter(response.shops, result => result.tier !== 'Tier1');
    const tier1Exact = [];
    const tier1NonExact = [];
    const tierNExact = [];
    const tierNNonExact = [];
    let tier1ExactDist = [];
    let tierNExactDist = [];
    let tier1NonExactDist = [];
    let tierNNonExactDist = [];
    // sort tier 1
    tier1.forEach(shop => {
      if (_startsWith(shop.name.toLowerCase(), this.shopName.toLowerCase())) {
        tier1Exact.push(shop);
      } else {
        tier1NonExact.push(shop);
      }
    });
    // sort tier 2 & 3
    tierN.forEach(shop => {
      if (_startsWith(shop.name.toLowerCase(), this.shopName.toLowerCase())) {
        tierNExact.push(shop);
      } else {
        tierNNonExact.push(shop);
      }
    });

    // Sort each section by distance
    tier1ExactDist = this.sortDistance(tier1Exact);
    tierNExactDist = this.sortDistance(tierNExact);
    tier1NonExactDist = this.sortDistance(tier1NonExact);
    tierNNonExactDist = this.sortDistance(tierNNonExact);
    const results = tier1ExactDist.concat(tierNExactDist, tier1NonExactDist, tierNNonExactDist);
    response.shops = results;
    return response;
  }

  private sortDistance(tier) {
    return _sortBy(tier, dis => Number(dis.distanceInfo.distance));
  }
}
